<template>
  <!-- Nothing to show until we know both that there is a user and which
       system they are looking at: a signed-out visitor otherwise sits in front
       of the loading skeleton forever, because the fetch never runs. -->
  <div v-if="signed_in && pws_id && (!loaded || show)" class="system-vitals mb-4">
    <h6 class="text-muted text-uppercase mb-3 small font-weight-bold">System Vitals</h6>

    <!-- Reserve the strip's height while loading. Without this the panel pops
         in after the fetch and shoves the rest of the dashboard down. -->
    <b-card v-if="!loaded" class="vitals-card border-0 shadow-sm" no-body aria-hidden="true">
      <div class="vitals-grid">
        <div v-for="n in 4" :key="n" class="vital-tile">
          <span class="skeleton skeleton-label"></span>
          <span class="skeleton skeleton-value"></span>
          <span class="skeleton skeleton-meta"></span>
        </div>
      </div>
    </b-card>

    <b-card v-else class="vitals-card border-0 shadow-sm" no-body>
      <div class="vitals-grid">

        <!-- Design capacity -->
        <div v-if="capacity_current" class="vital-tile" role="button" tabindex="0"
             @click="openDetail('capacity')" @keyup.enter="openDetail('capacity')">
          <span class="vital-label">Design Capacity Used</span>
          <span class="vital-value">
            {{ capacity_current.pct }}<span class="vital-unit">%</span>
          </span>
          <span class="vital-meta">
            {{ formatNumber(capacity_current.equiv) }} of
            {{ formatNumber(capacity_current.design) }} equiv.
          </span>
          <b-badge :variant="capacityVariant" class="vital-pill">
            <b-icon :icon="capacityIcon" class="mr-1" />{{ capacityLabel }}
          </b-badge>
          <svg
            v-if="capacity_spark.length > 1"
            class="vital-spark"
            viewBox="0 0 96 26"
            aria-hidden="true"
          >
            <polyline :points="sparkPoints(capacity_spark)" class="spark-line" />
            <circle :cx="94" :cy="sparkY(capacity_spark, lastOf(capacity_spark))" r="2.4" class="spark-cap" />
          </svg>
          <span class="vital-src">{{ capacity_history.length }} assessments</span>
        </div>

        <!-- Service connections -->
        <div v-if="connections_current !== null" class="vital-tile" role="button" tabindex="0"
             @click="openDetail('connections')" @keyup.enter="openDetail('connections')">
          <span class="vital-label">Service Connections</span>
          <span class="vital-value">{{ formatNumber(connections_current) }}</span>
          <span class="vital-meta">SDWIS inventory</span>
          <svg
            v-if="connections_spark.length > 1"
            class="vital-spark"
            viewBox="0 0 96 26"
            aria-hidden="true"
          >
            <polyline :points="sparkPoints(connections_spark, connections_current)" class="spark-line" />
            <circle
              :cx="94"
              :cy="sparkY(connections_spark, lastOf(connections_spark), connections_current)"
              r="2.4"
              class="spark-cap"
            />
            <circle
              :cx="94"
              :cy="sparkY(connections_spark, connections_current, connections_current)"
              r="3.2"
              class="spark-ref"
            />
          </svg>
          <span class="vital-src">{{ connections_spark.length }} inspections</span>
        </div>

        <!-- Population served -->
        <div v-if="population_current !== null" class="vital-tile" role="button" tabindex="0"
             @click="openDetail('population')" @keyup.enter="openDetail('population')">
          <span class="vital-label">Population Served</span>
          <span class="vital-value">{{ formatNumber(population_current) }}</span>
          <span class="vital-meta">SDWIS inventory</span>
          <svg
            v-if="population_spark.length > 1"
            class="vital-spark"
            viewBox="0 0 96 26"
            aria-hidden="true"
          >
            <polyline :points="sparkPoints(population_spark, population_current)" class="spark-line" />
            <circle
              :cx="94"
              :cy="sparkY(population_spark, lastOf(population_spark), population_current)"
              r="2.4"
              class="spark-cap"
            />
            <circle
              :cx="94"
              :cy="sparkY(population_spark, population_current, population_current)"
              r="3.2"
              class="spark-ref"
            />
          </svg>
          <span class="vital-src">{{ population_spark.length }} inspections</span>
        </div>

        <!-- Capacity assessment (CAF) -->
        <div v-if="caf" class="vital-tile">
          <span class="vital-label">Capacity Assessment</span>
          <span class="vital-value">
            {{ caf.score }}<span class="vital-unit">/100</span>
          </span>
          <span class="vital-meta">
            Grade {{ caf.grade }}<template v-if="caf.fiscal_year"> &middot; FY {{ caf.fiscal_year }}</template>
          </span>
          <b-badge :variant="cafVariant" class="vital-pill">
            <b-icon :icon="cafIcon" class="mr-1" />{{ cafLabel }}
          </b-badge>
        </div>

        <!-- Worth a Look. Rendered whenever the system has results at
             all, including when the count is zero: "nothing stood out" is the
             reassuring half of this feature and is worth saying out loud. It
             is skipped only when there are no results on record, because an
             empty tile there would read as a clean bill the system has not
             earned. -->
        <div
          v-if="flags && flags.has_results"
          class="vital-tile"
          role="button"
          tabindex="0"
          @click="goToResultFlags"
          @keyup.enter="goToResultFlags"
        >
          <span class="vital-label">Worth a Look</span>
          <span class="vital-value">{{ flags.total }}</span>
          <span class="vital-meta">{{ flagsMeta }}</span>
          <b-badge :variant="flagsVariant" class="vital-pill">
            <b-icon :icon="flagsIcon" class="mr-1" />{{ flagsLabel }}
          </b-badge>
          <span class="vital-src">View sample results</span>
        </div>

      </div>
    </b-card>

    <b-modal
      id="vital_detail_modal"
      :title="detail_title"
      size="lg"
      scrollable
    >
      <div v-if="detail_key">
        <div class="detail-headline">
          <span class="detail-figure">{{ detail_headline }}</span>
          <span class="detail-caption">{{ detail_caption }}</span>
        </div>

        <svg
          v-if="detail_series.length > 1"
          class="detail-chart"
          :viewBox="`0 0 ${CHART_W} ${CHART_H}`"
          role="img"
          :aria-label="detail_alt"
        >
          <line
            v-for="g in chartGridlines"
            :key="'g' + g.y"
            :x1="CHART_PAD_L" :x2="CHART_W - CHART_PAD_R"
            :y1="g.y" :y2="g.y"
            class="chart-grid"
          />
          <text
            v-for="g in chartGridlines"
            :key="'t' + g.y"
            :x="CHART_PAD_L - 8" :y="g.y + 4"
            text-anchor="end"
            class="chart-tick"
          >{{ g.label }}</text>

          <!-- Design capacity ceiling: stepped, because a ceiling holds until
               the system is rebuilt. Dashed so it is distinguishable without
               relying on colour. -->
          <polyline v-if="detail_ceiling" :points="detail_ceiling" class="chart-ceiling" />
          <polyline :points="detail_line" class="chart-line" />

          <text
            v-for="lbl in chartXLabels"
            :key="'x' + lbl.x"
            :x="lbl.x" :y="CHART_H - 8"
            text-anchor="middle"
            class="chart-tick"
          >{{ lbl.label }}</text>
        </svg>

        <p v-else class="text-muted small">
          Only one observation is on file, so there is no trend to plot yet.
        </p>

        <p class="detail-note small text-muted mb-0">{{ detail_note }}</p>
      </div>

      <template #modal-footer>
        <b-button
          v-if="detail_key === 'capacity'"
          variant="outline-primary"
          @click="openReports"
        >
          <b-icon icon="archive" /> View Calculation Reports
        </b-button>
        <b-button variant="primary" @click="$bvModal.hide('vital_detail_modal')">Close</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
/**
 * Dashboard vitals strip for the currently selected water system.
 *
 * Sourcing rule: the headline number comes from SDWIS (the state system of
 * record); the sparkline comes from the engineer reports, which reach further
 * back and are recorded more consistently. The two can legitimately disagree,
 * so the SDWIS value is drawn as a hollow reference ring rather than being
 * appended to the engineer series. See docs/POPULATION_AND_CONNECTIONS.md.
 *
 * The payload is cached in IndexedDB and served stale-while-revalidate, so a
 * return visit paints instantly instead of showing the skeleton again. The
 * underlying figures are assessments and annual counts -- they change a few
 * times a year -- so a short-lived stale read costs nothing, and the refetch
 * that follows corrects it within the same visit.
 */
import * as idb from 'idb-keyval'

// Bump when the shape of the cached payload changes, so old entries are
// ignored rather than half-read. v2 added result_flags; without the bump a
// returning user would paint a cached v1 payload and silently show no
// Worth a Look tile until the refetch landed.
const CACHE_VERSION = 2
// Past this, prefer the skeleton over a stale number.
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000

export default {
  name: 'SystemVitals',
  data() {
    return {
      capacity_current: null,
      capacity_history: [],
      connections_current: null,
      connections_history: [],
      population_current: null,
      population_history: [],
      caf: null,
      flags: null,
      loaded: false,
      detail_key: '',
      CHART_W: 720,
      CHART_H: 260,
      CHART_PAD_L: 52,
      CHART_PAD_R: 16,
      CHART_PAD_T: 12,
      CHART_PAD_B: 28,
    }
  },
  computed: {
    pws_id() {
      return this.$store.state.pws_id
    },
    signed_in() {
      return this.$store.state.signed_in
    },
    show() {
      return this.loaded && (
        this.capacity_current ||
        this.connections_current !== null ||
        this.population_current !== null ||
        this.caf ||
        (this.flags && this.flags.has_results)
      )
    },
    // The tier vocabulary is the model's, so these read worst_tier rather
    // than re-deriving severity from the counts.
    flagsVariant() {
      if (!this.flags) return 'secondary'
      if (this.flags.worst_tier === 'OVER_LIMIT') return 'danger'
      if (this.flags.worst_tier === 'APPROACHING') return 'warning'
      if (this.flags.worst_tier === 'DETECTED') return 'info'
      return 'success'
    },
    flagsIcon() {
      if (!this.flags || !this.flags.worst_tier) return 'check-circle-fill'
      if (this.flags.worst_tier === 'OVER_LIMIT') return 'exclamation-triangle-fill'
      if (this.flags.worst_tier === 'APPROACHING') return 'exclamation-circle-fill'
      return 'info-circle-fill'
    },
    flagsLabel() {
      if (!this.flags || !this.flags.worst_tier) return 'Nothing stood out'
      if (this.flags.worst_tier === 'OVER_LIMIT') return 'Above a limit'
      if (this.flags.worst_tier === 'APPROACHING') return 'Close to a limit'
      return 'Detected'
    },
    flagsMeta() {
      if (!this.flags) return ''
      // Never the word "violation": this panel makes no compliance claim, and
      // the vocabulary is how that promise is kept. See ResultFlags.vue.
      // At zero the meta says what we did, not what we found — repeating the
      // tile's own label back at the reader tells them nothing, and the tab is
      // named for the invitation, not for a promise that something is wrong.
      if (!this.flags.total) return 'We checked your recent results'
      const c = this.flags.counts || {}
      const parts = []
      if (c.OVER_LIMIT) parts.push(c.OVER_LIMIT + ' above a limit')
      if (c.APPROACHING) parts.push(c.APPROACHING + ' close')
      if (c.DETECTED) parts.push(c.DETECTED + ' detected')
      return parts.join(' · ')
    },
    capacity_spark() {
      return this.capacity_history.map(h => h.pct)
    },
    connections_spark() {
      return this.connections_history.map(h => h.value)
    },
    population_spark() {
      return this.population_history.map(h => h.value)
    },
    capacityVariant() {
      const pct = this.capacity_current ? this.capacity_current.pct : 0
      if (pct >= 100) return 'danger'
      if (pct >= 80) return 'warning'
      return 'success'
    },
    capacityIcon() {
      const pct = this.capacity_current ? this.capacity_current.pct : 0
      return pct >= 80 ? 'exclamation-triangle-fill' : 'check-circle-fill'
    },
    capacityLabel() {
      const pct = this.capacity_current ? this.capacity_current.pct : 0
      if (pct >= 100) return 'At or over capacity'
      if (pct >= 80) return 'Approaching capacity'
      return 'Capacity available'
    },
    /**
     * Letter grade, falling back to the documented score bands when the view
     * hasn't supplied one. Bands: A+ 95, A 90, B+ 85, B 80, C+ 75, C 70,
     * D+ 65, D 60, F below.
     */
    cafGrade() {
      if (!this.caf) return ''
      if (this.caf.grade) return this.caf.grade
      const s = this.caf.score
      if (s === null || s === undefined) return ''
      if (s >= 95) return 'A+'
      if (s >= 90) return 'A'
      if (s >= 85) return 'B+'
      if (s >= 80) return 'B'
      if (s >= 75) return 'C+'
      if (s >= 70) return 'C'
      if (s >= 65) return 'D+'
      if (s >= 60) return 'D'
      return 'F'
    },
    cafVariant() {
      const grade = this.cafGrade
      if (['A+', 'A', 'B+', 'B'].indexOf(grade) !== -1) return 'success'
      if (['C+', 'C', 'D+', 'D'].indexOf(grade) !== -1) return 'warning'
      return 'danger'
    },
    cafIcon() {
      if (this.caf && this.caf.score === 100) return 'trophy-fill'
      const grade = this.cafGrade
      if (['A+', 'A'].indexOf(grade) !== -1) return 'star-fill'
      if (['B+', 'B'].indexOf(grade) !== -1) return 'check-circle-fill'
      return 'exclamation-triangle-fill'
    },
    /* ---- drill-down detail ---- */
    detail_config() {
      return {
        capacity: {
          title: 'Design Capacity',
          series: this.capacity_history.map(h => ({ as_of: h.assessed, value: h.pct })),
          headline: this.capacity_current ? `${this.capacity_current.pct}%` : '',
          caption: this.capacity_current
            ? `${this.formatNumber(this.capacity_current.equiv)} of ` +
              `${this.formatNumber(this.capacity_current.design)} equivalent connections, ` +
              `assessed ${this.capacity_current.assessed}`
            : '',
          note: 'Percent of design capacity at each assessment. The dashed line is ' +
                'the design capacity itself, which steps up when the system builds ' +
                'and can fall when a well is re-tested or derated.',
          unit: '%',
        },
        connections: {
          title: 'Service Connections',
          series: this.connections_history,
          headline: this.formatNumber(this.connections_current),
          caption: 'Current count from the SDWIS inventory',
          note: 'The line is the count recorded at each engineer inspection. The ' +
                'headline figure comes from SDWIS, the state system of record, so ' +
                'the two can differ.',
          unit: '',
        },
        population: {
          title: 'Population Served',
          series: this.population_history,
          headline: this.formatNumber(this.population_current),
          caption: 'Current count from the SDWIS inventory',
          note: 'The line is the population recorded at each engineer inspection. ' +
                'The headline figure comes from SDWIS, the state system of record, ' +
                'so the two can differ.',
          unit: '',
        },
      }[this.detail_key] || null
    },
    detail_title() {
      return this.detail_config ? this.detail_config.title : ''
    },
    detail_headline() {
      return this.detail_config ? this.detail_config.headline : ''
    },
    detail_caption() {
      return this.detail_config ? this.detail_config.caption : ''
    },
    detail_note() {
      return this.detail_config ? this.detail_config.note : ''
    },
    detail_series() {
      return this.detail_config ? this.detail_config.series : []
    },
    detail_alt() {
      if (!this.detail_config || this.detail_series.length < 2) return ''
      const first = this.detail_series[0]
      const last = this.detail_series[this.detail_series.length - 1]
      const u = this.detail_config.unit
      return `${this.detail_config.title} from ${first.as_of} to ${last.as_of}, ` +
             `${first.value}${u} to ${last.value}${u}.`
    },
    /** Shared y-domain for the detail chart, padded to a round top. */
    chartDomain() {
      const values = this.detail_series.map(p => p.value)
      // Keep the 100% reference on screen even for a system well under it.
      if (this.detail_key === 'capacity') {
        values.push(100)
      }
      const max = Math.max(...values, 1)
      const step = Math.pow(10, Math.floor(Math.log10(max))) / 2
      const top = Math.ceil(max / step) * step
      return { min: 0, max: top || 1 }
    },
    chartGridlines() {
      const { max } = this.chartDomain
      const lines = []
      for (let i = 0; i <= 4; i++) {
        const value = (max / 4) * i
        lines.push({
          y: Number(this.chartY(value).toFixed(1)),
          label: this.formatNumber(value),
        })
      }
      return lines
    },
    chartXLabels() {
      const n = this.detail_series.length
      if (n < 2) return []
      const idxs = [0, Math.floor((n - 1) / 2), n - 1]
      return idxs.map(i => ({
        x: Number(this.chartX(i).toFixed(1)),
        label: (this.detail_series[i].as_of || '').slice(0, 4),
      }))
    },
    detail_line() {
      return this.detail_series
        .map((p, i) => `${this.chartX(i).toFixed(1)},${this.chartY(p.value).toFixed(1)}`)
        .join(' ')
    },
    /**
     * The design-capacity ceiling. Plotted as a percentage the ceiling is by
     * definition a constant 100%, so this is a straight reference line — the
     * series crossing it is the system going over capacity.
     */
    detail_ceiling() {
      if (this.detail_key !== 'capacity' || this.detail_series.length < 2) return ''
      const y = this.chartY(100).toFixed(1)
      const right = this.CHART_W - this.CHART_PAD_R
      return `${this.CHART_PAD_L},${y} ${right},${y}`
    },
    cafLabel() {
      if (!this.caf) return ''
      // A perfect score is its own thing — 155 systems have earned one.
      if (this.caf.score === 100) return 'Perfect score'
      const grade = this.cafGrade
      if (grade === 'A+') return 'Excellent'
      if (grade === 'A') return 'Very good'
      if (['B+', 'B'].indexOf(grade) !== -1) return 'Satisfactory'
      if (['C+', 'C'].indexOf(grade) !== -1) return 'Needs improvement'
      if (['D+', 'D'].indexOf(grade) !== -1) return 'Marginal'
      return 'Action required'
    },
  },
  created() {
    this.fetchVitals()
  },
  watch: {
    pws_id() {
      // Drop the previous system's figures rather than leaving them on screen
      // under the new system's name.
      this.loaded = false
      this.capacity_current = null
      this.capacity_history = []
      this.connections_current = null
      this.connections_history = []
      this.population_current = null
      this.population_history = []
      this.caf = null
      this.flags = null
      this.fetchVitals()
    },
  },
  methods: {
    cacheKey(pws_id) {
      return `vitals_${CACHE_VERSION}_${pws_id}`
    },
    applyVitals(data) {
      this.capacity_current = data.capacity ? data.capacity.current : null
      this.capacity_history = (data.capacity && data.capacity.history) || []
      this.connections_current = data.connections ? data.connections.current : null
      this.connections_history = (data.connections && data.connections.history) || []
      this.population_current = data.population ? data.population.current : null
      this.population_history = (data.population && data.population.history) || []
      this.caf = data.caf || null
      // Absent when the summary failed server-side; the tile just does not
      // render. Never coerced to zero — "no flags" and "we could not look"
      // are different statements.
      this.flags = data.result_flags || null
      this.loaded = true
    },
    async readCache(pws_id) {
      try {
        const entry = await idb.get(this.cacheKey(pws_id))
        if (!entry || !entry.data) {
          return null
        }
        if (Date.now() - entry.at > CACHE_MAX_AGE_MS) {
          return null
        }
        return entry.data
      } catch (error) {
        // Private-browsing modes and storage pressure can both make IndexedDB
        // unavailable. Losing the cache is not losing the feature.
        console.error('Error reading cached vitals', error)
        return null
      }
    },
    async fetchVitals() {
      const pws_id = this.pws_id
      if (!pws_id) {
        return
      }

      // Paint whatever we already know, then correct it.
      const cached = await this.readCache(pws_id)
      if (cached && pws_id === this.pws_id) {
        this.applyVitals(cached)
      }

      try {
        const response = await this.axios.get('api/vitals', {
          params: { pws_id: pws_id }
        })
        // The user can switch systems while this is in flight; a late reply
        // for the previous system must not overwrite the current one.
        if (pws_id !== this.pws_id) {
          return
        }
        this.applyVitals(response.data)
        idb.set(this.cacheKey(pws_id), { at: Date.now(), data: response.data })
          .catch(error => console.error('Error caching vitals', error))
      } catch (error) {
        // A vitals failure must never take the dashboard down with it. If the
        // cache already painted, the stale figures stay up rather than
        // collapsing the strip.
        console.error('Error fetching system vitals', error)
      } finally {
        if (pws_id === this.pws_id) {
          this.loaded = true
        }
      }
    },
    goToResultFlags() {
      // The tab sits last, so landing on /sample-results alone drops the user
      // on Compliance Reports with no sign of what they clicked. The query
      // param is read by SampleResults; an unknown value is ignored there.
      this.$router.push({
        path: '/sample-results',
        query: { tab: 'worth-a-look' }
      }).catch(() => {
        // vue-router rejects a push to the route you are already on.
      })
    },
    formatNumber(n) {
      if (n === null || n === undefined) {
        return '—'
      }
      return Math.round(n).toLocaleString()
    },
    lastOf(values) {
      return values[values.length - 1]
    },
    openDetail(key) {
      this.detail_key = key
      this.$bvModal.show('vital_detail_modal')
    },
    openReports() {
      this.$bvModal.hide('vital_detail_modal')
      this.$router.push({ name: 'PWSFiles', query: { category: 'design_capacity' } })
    },
    /** Map a series index to an x coordinate in the detail chart. */
    chartX(i) {
      const n = this.detail_series.length
      const inner = this.CHART_W - this.CHART_PAD_L - this.CHART_PAD_R
      if (n < 2) return this.CHART_PAD_L + inner / 2
      return this.CHART_PAD_L + (i / (n - 1)) * inner
    },
    /** Map a value to a y coordinate in the detail chart. */
    chartY(value) {
      const { min, max } = this.chartDomain
      const inner = this.CHART_H - this.CHART_PAD_T - this.CHART_PAD_B
      const span = (max - min) || 1
      return this.CHART_PAD_T + (1 - (value - min) / span) * inner
    },
    /**
     * Scale one value into the sparkline's 96x26 viewBox. `ref` is included in
     * the domain when present so the SDWIS marker never falls outside the box.
     */
    sparkY(values, value, ref) {
      const pad = 2
      const all = (ref === null || ref === undefined) ? values : values.concat([ref])
      const min = Math.min(...all)
      const max = Math.max(...all)
      const span = (max - min) || 1
      return pad + (1 - (value - min) / span) * (26 - pad * 2)
    },
    sparkPoints(values, ref) {
      const pad = 2
      return values
        .map((v, i) => {
          const x = pad + (i / (values.length - 1)) * (96 - pad * 2)
          return `${x.toFixed(1)},${this.sparkY(values, v, ref).toFixed(1)}`
        })
        .join(' ')
    },
  },
}
</script>

<style scoped>
.vitals-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.vitals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1px;
  background-color: #e9ecef;
}

.vital-tile {
  background-color: #fff;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.vital-label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #868e96;
}

.vital-value {
  font-size: 1.85rem;
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: #212529;
}

.vital-unit {
  font-size: 1.1rem;
  font-weight: 600;
}

.vital-meta {
  font-size: 0.78rem;
  color: #868e96;
  font-variant-numeric: tabular-nums;
}

.vital-pill {
  align-self: flex-start;
  margin-top: 0.3rem;
  font-weight: 600;
}

.vital-spark {
  display: block;
  width: 96px;
  height: 26px;
  margin-top: 0.4rem;
}

.spark-line {
  fill: none;
  stroke: #2a78d6;
  stroke-width: 1.6;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.spark-cap {
  fill: #2a78d6;
}

/* The SDWIS current value. Hollow so it reads as a separate source: when it
   agrees with the engineer series it sits on the endpoint, and when it does
   not, the gap is visible rather than smoothed away. */
.spark-ref {
  fill: #fff;
  stroke: #2a78d6;
  stroke-width: 1.6;
}

.vital-src {
  margin-top: auto;
  padding-top: 0.5rem;
  font-size: 0.66rem;
  letter-spacing: 0.03em;
  color: #adb5bd;
}

/* ---- interactive tiles ---- */
.vital-tile[role="button"] {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.vital-tile[role="button"]:hover {
  background-color: #f8f9fa;
}

.vital-tile[role="button"]:focus-visible {
  outline: 2px solid #2a78d6;
  outline-offset: -2px;
}

/* ---- loading skeleton ---- */
.skeleton {
  display: block;
  border-radius: 4px;
  background: linear-gradient(90deg, #edeff2 25%, #f6f7f9 37%, #edeff2 63%);
  background-size: 400% 100%;
  animation: vitals-shimmer 1.4s ease infinite;
}

.skeleton-label { width: 62%; height: 9px; margin-bottom: 0.55rem; }
.skeleton-value { width: 45%; height: 26px; margin-bottom: 0.5rem; }
.skeleton-meta  { width: 78%; height: 10px; }

@keyframes vitals-shimmer {
  0%   { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton {
    animation: none;
  }
}

/* ---- detail modal ---- */
.detail-headline {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0 0.6rem;
  margin-bottom: 1rem;
}

.detail-figure {
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: #212529;
}

.detail-caption {
  font-size: 0.82rem;
  color: #868e96;
}

.detail-chart {
  display: block;
  width: 100%;
  height: auto;
  margin-bottom: 0.75rem;
}

.chart-grid {
  stroke: #eceef1;
  stroke-width: 1;
}

.chart-tick {
  fill: #868e96;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.chart-line {
  fill: none;
  stroke: #2a78d6;
  stroke-width: 2;
  stroke-linejoin: round;
  stroke-linecap: round;
}

/* Dashed, so the ceiling is distinguishable from the series without colour. */
.chart-ceiling {
  fill: none;
  stroke: #adb5bd;
  stroke-width: 2;
  stroke-dasharray: 5 4;
}

.detail-note {
  line-height: 1.5;
}
</style>
